<template>
  <header class="portal-header" :class="{ 'is-compact': isCompact, 'is-landing': isLanding, 'is-info': isInfoPage }">
    <div class="portal-header__top">
      <div class="portal-header__top-inner">
        <button
          type="button"
          class="portal-header__burger"
          aria-label="전체 메뉴 열기"
          @click="drawerOpen = true"
        >
          <AppIcon icon="mdi-menu" :size="24" />
        </button>

        <div class="portal-header__brand">
          <router-link :to="logoHomeTo" class="portal-header__logo">
            <img src="/favicon.svg" width="34" height="34" alt="" aria-hidden="true" />
          </router-link>
          <div class="portal-header__logo-line">
            <router-link :to="logoHomeTo" class="portal-header__logo-name">
              장기요양지원센터
            </router-link>
          </div>
        </div>

        <nav class="portal-header__nav" aria-label="주요 메뉴">
          <router-link
            v-if="isAdminLoggedIn"
            to="/info"
            class="portal-header__menu-link"
            :class="{ 'is-on': infoActive }"
            :aria-current="infoActive ? 'page' : undefined"
          >
            정보마당
          </router-link>
          <router-link
            :to="MALL_HOME_PATH"
            target="_blank"
            rel="noopener noreferrer"
            class="portal-header__menu-link"
            :class="{ 'is-on': mallActive }"
          >
            함우리몰
          </router-link>
        </nav>

        <div ref="utilRef" class="portal-header__util">
          <template v-if="isLoggedIn && member">
            <div
              class="portal-header__util-item"
              @mouseenter="openUser"
              @mouseleave="closeUserSoon"
            >
              <button
                type="button"
                class="portal-header__profile"
                :class="{ 'is-open': userOpen }"
                :aria-expanded="userOpen"
                :title="memberPersonName"
                @click="userOpen = !userOpen"
              >
                <AppIcon icon="mdi-account-circle" :size="20" />
                <span class="portal-header__welcome">{{ welcomeName }}님</span>
                <AppIcon icon="mdi-chevron-down" :size="16" />
              </button>
              <div v-if="userOpen" class="portal-header__panel portal-header__panel--right">
                <p class="portal-header__who">{{ memberPersonName }}</p>
                <router-link to="/my-info" @click="userOpen = false">마이페이지</router-link>
                <button type="button" class="is-logout" @click="onLogout">로그아웃</button>
              </div>
            </div>
          </template>
          <template v-else-if="isSellerLoggedIn && seller">
            <div
              class="portal-header__util-item"
              @mouseenter="openUser"
              @mouseleave="closeUserSoon"
            >
              <button
                type="button"
                class="portal-header__profile"
                :class="{ 'is-open': userOpen }"
                :aria-expanded="userOpen"
                :title="sellerPersonName"
                @click="userOpen = !userOpen"
              >
                <AppIcon icon="mdi-storefront-outline" :size="20" />
                <span class="portal-header__welcome">{{ sellerWelcome }}</span>
                <AppIcon icon="mdi-chevron-down" :size="16" />
              </button>
              <div v-if="userOpen" class="portal-header__panel portal-header__panel--right">
                <p class="portal-header__who">{{ sellerPersonName }}</p>
                <router-link :to="managePageTo || '/seller'" @click="userOpen = false">
                  관리자페이지
                </router-link>
                <button type="button" class="is-logout" @click="onSellerLogout">로그아웃</button>
              </div>
            </div>
          </template>
          <template v-else-if="isAdminLoggedIn && admin">
            <div
              class="portal-header__util-item"
              @mouseenter="openUser"
              @mouseleave="closeUserSoon"
            >
              <button
                type="button"
                class="portal-header__profile"
                :class="{ 'is-open': userOpen }"
                :aria-expanded="userOpen"
                :title="adminPersonName"
                @click="userOpen = !userOpen"
              >
                <AppIcon icon="mdi-shield-crown-outline" :size="20" />
                <span class="portal-header__welcome">{{ adminWelcome }}님</span>
                <AppIcon icon="mdi-chevron-down" :size="16" />
              </button>
              <div v-if="userOpen" class="portal-header__panel portal-header__panel--right">
                <p class="portal-header__who">{{ adminPersonName }}</p>
                <router-link to="/admin" @click="userOpen = false">관리자페이지</router-link>
                <button type="button" class="is-logout" @click="onAdminLogout">로그아웃</button>
              </div>
            </div>
          </template>
          <template v-else>
            <button type="button" class="portal-header__util-link" @click="onPortalLogin">
              로그인
            </button>
          </template>
        </div>
      </div>
    </div>

    <Transition name="drawer-fade">
      <div v-if="drawerOpen" class="portal-drawer__backdrop" @click="drawerOpen = false" />
    </Transition>
    <Transition name="drawer-slide">
      <aside v-if="drawerOpen" class="portal-drawer" aria-label="전체 메뉴">
        <div class="portal-drawer__head">
          <span class="portal-drawer__brand">메뉴</span>
          <button type="button" class="portal-drawer__close" aria-label="닫기" @click="drawerOpen = false">
            <AppIcon icon="mdi-close" :size="22" />
          </button>
        </div>
        <nav class="portal-drawer__nav">
          <router-link
            v-if="isAdminLoggedIn"
            to="/info"
            class="portal-drawer__link"
            @click="drawerOpen = false"
          >
            정보마당
          </router-link>
          <router-link
            :to="MALL_HOME_PATH"
            target="_blank"
            rel="noopener noreferrer"
            class="portal-drawer__link"
            @click="drawerOpen = false"
          >
            함우리몰
          </router-link>
        </nav>
        <div class="portal-drawer__foot">
          <template v-if="isLoggedIn">
            <router-link to="/my-info" class="portal-btn portal-btn--secondary" @click="drawerOpen = false">마이페이지</router-link>
            <button type="button" class="portal-btn portal-btn--primary" @click="onLogout">로그아웃</button>
          </template>
          <template v-else-if="isSellerLoggedIn">
            <router-link :to="managePageTo || '/seller'" class="portal-btn portal-btn--secondary" @click="drawerOpen = false">관리자페이지</router-link>
            <button type="button" class="portal-btn portal-btn--primary" @click="onSellerLogout">로그아웃</button>
          </template>
          <template v-else-if="isAdminLoggedIn">
            <router-link to="/admin" class="portal-btn portal-btn--secondary" @click="drawerOpen = false">관리자페이지</router-link>
            <button type="button" class="portal-btn portal-btn--primary" @click="onAdminLogout">로그아웃</button>
          </template>
          <template v-else>
            <button type="button" class="portal-btn portal-btn--secondary" @click="onPortalLogin">로그인</button>
          </template>
        </div>
      </aside>
    </Transition>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { MALL_HOME_PATH } from '@/constants/mall'
import { goPortalLogin } from '@/composables/useLoginDestination'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useSellerCenter } from '@/composables/useSellerCenter'
import { staffManagePath } from '@/composables/useUnifiedLogin'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useMemberAuth } from '@/composables/useMemberAuth'

const route = useRoute()
const router = useRouter()
const { isLoggedIn, member, logout, initMemberAuth } = useMemberAuth()
const { admin, isAdminLoggedIn, initAdminAuth, logout: logoutAdmin } = useAdminAuth()
const { seller, refreshSellerAuth } = useSellerCenter()
const { isSellerLoggedIn, logout: logoutSeller } = useSellerAuth()
const managePageTo = computed(() =>
  staffManagePath({
    isAdmin: isAdminLoggedIn.value,
    isSeller: isSellerLoggedIn.value,
    seller: seller.value,
  }),
)

const drawerOpen = ref(false)
const userOpen = ref(false)
const isCompact = ref(false)
const utilRef = ref<HTMLElement | null>(null)
let userCloseTimer = 0

function openUser() {
  window.clearTimeout(userCloseTimer)
  userOpen.value = true
}

function closeUserSoon() {
  window.clearTimeout(userCloseTimer)
  userCloseTimer = window.setTimeout(() => {
    userOpen.value = false
  }, 160)
}

const logoHomeTo = '/'
const isLanding = computed(() =>
  ['dashboard', 'terms', 'login', 'info-plaza', 'info-detail', 'info-center', 'info-story'].includes(String(route.name ?? '')),
)
const infoActive = computed(
  () => route.path === '/info' || route.path.startsWith('/info/'),
)
const isInfoPage = computed(() => route.name === 'info-center' || route.name === 'info-detail' || route.name === 'info-story')

function onPortalLogin() {
  drawerOpen.value = false
  goPortalLogin(router, route)
}

const memberPersonName = computed(
  () => member.value?.bossName?.trim() || member.value?.userId || '회원',
)
const welcomeName = computed(
  () => member.value?.orgName?.trim() || memberPersonName.value,
)
const sellerPersonName = computed(
  () =>
    seller.value?.ownerName?.trim() ||
    seller.value?.managerName?.trim() ||
    seller.value?.storeName?.trim() ||
    seller.value?.sellerId ||
    '판매업체',
)
const sellerWelcome = computed(
  () => seller.value?.storeName?.trim() || sellerPersonName.value,
)
const adminPersonName = computed(
  () => admin.value?.name?.trim() || admin.value?.userId || '관리자',
)
const adminWelcome = computed(() => adminPersonName.value)

const mallActive = computed(
  () => route.path === MALL_HOME_PATH || route.path.startsWith(`${MALL_HOME_PATH}/`),
)

const onScroll = () => {
  isCompact.value = window.scrollY > 24
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  userOpen.value = false
  drawerOpen.value = false
}

const onDocClick = (event: MouseEvent) => {
  const target = event.target as Node
  if (userOpen.value && utilRef.value && !utilRef.value.contains(target)) {
    userOpen.value = false
  }
}

watch(
  () => route.fullPath,
  () => {
    userOpen.value = false
    drawerOpen.value = false
  },
)

watch(drawerOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})

onMounted(() => {
  initMemberAuth()
  void initAdminAuth()
  refreshSellerAuth()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('keydown', onKeydown)
  document.addEventListener('click', onDocClick)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('keydown', onKeydown)
  document.removeEventListener('click', onDocClick)
  document.body.style.overflow = ''
  window.clearTimeout(userCloseTimer)
})

async function onLogout() {
  userOpen.value = false
  drawerOpen.value = false
  await logout()
  router.push('/')
}

async function onSellerLogout() {
  userOpen.value = false
  drawerOpen.value = false
  await logoutSeller()
}

async function onAdminLogout() {
  userOpen.value = false
  drawerOpen.value = false
  await logoutAdmin()
}
</script>

<style lang="scss" src="./PortalHeader.scss"></style>
